import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useState, useMemo } from "react";
import { FlatList } from "react-native";
import { usePaginationFragment } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import { MemberModalRow } from "./MemberModalRow";
import { MemberModalSelectedMember } from "./MemberModalSelectedMember";
import { MemberModalMemberListFragment$key } from "./__generated__/MemberModalMemberListFragment.graphql";

type MemberModalMemberListProps = {
  memberListRef: MemberModalMemberListFragment$key;
  isOpen: boolean;
};

export const MemberModalMemberList = ({
  memberListRef,
  isOpen,
}: MemberModalMemberListProps) => {
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    MemberModalMemberListFragmentString,
    memberListRef
  );

  const [selectedMemberId, setSelectedMemberId] = useState<String | null>(null);

  const memoizedData = useMemo(() => {
    return data.members?.edges || [];
  }, [data.members?.edges]);

  const selectedMember =
    memoizedData.find((e) => e?.node?.id === selectedMemberId)?.node ?? null;

  /**
   * When the modal closes, we clear out the selected member ID
   */
  useEffect(() => {
    if (!isOpen) {
      setSelectedMemberId(null);
    }
  }, [isOpen]);

  return (
    <Box flex={1} pb={6}>
      {memoizedData.length ? (
        <FlatList
          style={{ paddingHorizontal: 16, flex: 1 }}
          data={memoizedData}
          keyExtractor={(item) => `${item?.node?.id}`}
          renderItem={(data) => {
            const node = data.item?.node;
            return node ? (
              <MemberModalRow memberRef={node} onPress={setSelectedMemberId} />
            ) : null;
          }}
          onEndReached={() => {
            if (hasNext) loadNext(20);
          }}
          onEndReachedThreshold={0.5}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <Box flex={1} justifyContent="center" alignItems="center">
          <Text variant="mainTitle2">{t`No members found`}</Text>
        </Box>
      )}
      {isLoadingNext ? (
        <Box
          width="100%"
          height={50}
          justifyContent="center"
          alignItems="center"
        >
          <Loader />
        </Box>
      ) : null}
      <MemberModalSelectedMember
        memberRef={selectedMember}
        onClose={() => {
          setSelectedMemberId(null);
        }}
      />
    </Box>
  );
};

export const MemberModalMemberListFragmentString = graphql`
  fragment MemberModalMemberListFragment on Club
  @refetchable(queryName: "MemberModalMemberListQuery") {
    members(first: $first, after: $after, search: $search, active: true)
      @connection(key: "MemberModalMemberList_members") {
      edges {
        node {
          id
          ...MemberModalRow_Member
          ...MemberModalSelectedMember_Member
        }
      }
    }
  }
`;
